import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../context/AuthContext";
import { validateOfferToken, counterOffer } from "../services/api";
import { FONTS, COLORS, BREAKPOINTS } from "../utils/styleConstants";
import SidebarLogo from "../assets/sidebar_logo.svg";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import LoadingIndicator from "../components/LoadingIndicator";
import NotificationMessage from "../components/NotificationMessage";
import OfferDisplay from "../components/oferta/OfferDisplay";
import PreviewModal from "../components/oferta/PreviewModal";
import RichTextEditor from "../components/common/RichTextEditor";

const DARK_BLUE = "#20376e";
const ACCENT_ORANGE = "#ff7057";

// Main container styles
const PageContainer = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  background-color: var(--secondary-color);
  display: flex;
  box-shadow: var(--box-shadow);
  overflow: hidden;
  height: 100vh;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    overflow: visible;
    flex-direction: column;
    box-shadow: none;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 2rem 3rem;
  border-radius: 1rem 0 0 1rem;
  background: white;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    height: auto;
    min-height: calc(100vh - 70px);
    padding: 1.5rem;
    border-radius: 2rem 2rem 0 0;
    overflow-y: auto;
    background: white;
  }
`;

const SidebarLogoStyled = styled.img`
  width: 40px;
  height: auto;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    width: 30px;
    margin-bottom: 0;
    margin-left: 1rem;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TwoColumnLayout = styled.div`
  display: flex;
  gap: 2rem;
  flex: 1;
  
  @media (max-width: ${BREAKPOINTS.TABLET}) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  max-width: 50%;
  
  @media (max-width: ${BREAKPOINTS.TABLET}) {
    max-width: 100%;
  }
`;

const RightColumn = styled.div`
  flex: 1;
  max-width: 50%;
  
  @media (max-width: ${BREAKPOINTS.TABLET}) {
    max-width: 100%;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: ${COLORS.PRIMARY};
  font-size: 2.5rem;
  font-weight: 600;
  font-family: ${FONTS.PRIMARY};
  margin: 0 0 0.5rem 0;

  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    font-size: 1.8rem;
  }
`;

const Description = styled.p`
  color: ${ACCENT_ORANGE};
  font-size: 1.1rem;
  font-family: ${FONTS.PRIMARY};
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.5;
  width: 100%;
  max-width: 70%;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    max-width: 100%;
    font-size: 1rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.9rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 500;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  
  &:after {
    content: ${props => props.required ? '"*"' : '""'};
    color: ${COLORS.PRIMARY};
    margin-left: 4px;
  }
`;

// Confidentiality toggle container matching OfertaStep2
const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-left: auto;
  margin-bottom: 1rem;
  border-bottom: 1px solid #c1d3f0;
  padding-bottom: 1rem;
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100px;
`;

const Select = styled.select`
  appearance: none;
  width: 100%;
  padding: 8px 12px;
  font-size: 0.9rem;
  border: none;
  border-radius: 4px;
  background-color: white;
  color: ${COLORS.TEXT};
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    width: 100%;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  
  svg {
    width: 12px;
    height: 12px;
    fill: ${COLORS.SECONDARY};
  }
`;

const HelpButtonContainer = styled.div`
  position: relative;
  display: inline-flex;
  margin-left: 8px;
  flex-shrink: 0;
  
  &:hover span {
    visibility: visible;
    opacity: 1;
  }
`;

const HelpButton = styled.button`
  background-color: #75a9f9;
  color: white;
  border: none;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  font-size: 0.6rem;
  font-family: ${FONTS.SECONDARY};
  font-weight: bold;
  cursor: help;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 0;
  line-height: 1;
`;

const TooltipText = styled.span`
  visibility: hidden;
  width: auto;
  max-width: 450px;
  min-width: 400px;
  background-color: #c1d3f0;
  color: ${COLORS.SECONDARY};
  text-align: left;
  border-radius: 1rem;
  padding: 0.75rem 1.25rem;
  position: absolute;
  z-index: 100;
  bottom: 20px;
  left: -10px;
  opacity: 0;
  transition: opacity 0.3s;
  font-weight: normal;
  font-size: 0.85rem;
  font-family: ${FONTS.SECONDARY};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  line-height: 1.4;
  
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 10px;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid #c1d3f0;
  }
`;

const TextAreaContainer = styled.div`
  position: relative;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 300px;
  border: 1px solid #75a9f9;
  border-radius: 1rem;
  padding: 1rem;
  font-size: 0.9rem;
  font-family: ${FONTS.SECONDARY};
  resize: none;
  margin-bottom: 0.5rem;
  
  &:focus {
    outline: none;
    border-color: #5a94f5;
  }
  
  &::placeholder {
    color: #a0aec0;
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1d3f0;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8c1e9;
  }
`;

// Checkbox for terms
const ConfirmationCheckbox = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  label {
    font-size: 0.9rem;
    font-family: ${FONTS.SECONDARY};
    color: ${COLORS.SECONDARY};
    cursor: pointer;
    line-height: 1.4;
  }
`;

const CheckboxInput = styled.input`
  margin-right: 8px;
  margin-top: 0.1rem;
  cursor: pointer;
  transform: scale(0.8);

  &:disabled {
    cursor: not-allowed;
  }
`;

// Updated button styles to match OfertaStep2
const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 1.5rem;
  font-weight: 500;
  font-size: 0.95rem;
  font-family: ${FONTS.SECONDARY};
  cursor: pointer;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #ff7057;
  color: white;
  border: none;
  box-shadow: 0 2px 4px rgba(255, 112, 87, 0.2);
  
  &:hover {
    background-color: #ff5b40;
  }
  
  &:disabled {
    background-color: #ffc1ad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const SecondaryButton = styled(Button)`
  background-color: white;
  color: #ff7057;
  border: 1px solid #ff7057;
  box-shadow: 0 1px 2px rgba(255, 112, 87, 0.1);
  
  &:hover {
    background-color: #fff8f6;
  }
  
  &:disabled {
    color: #ffb8a8;
    border-color: #ffb8a8;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const OutlineButton = styled(Button)`
  background-color: white;
  color: #a8b9d5;
  border: 1px solid #dbe4f2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: #f5f8fd;
  }
  
  &:focus {
    outline: none;
  }
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
`;

const ConfirmationContent = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const ConfirmationText = styled.p`
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

// Updated button container styles
const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  position: relative;
  margin-top: 2rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: ${BREAKPOINTS.MOBILE}) {
    flex-direction: column;
    width: 100%;
  }
`;

// Add SectionTitle style from OfertaStep2
const SectionTitle = styled.h2`
  font-size: 1.1rem;
  color: ${COLORS.SECONDARY};
  font-family: ${FONTS.SECONDARY};
  font-weight: 600;
  min-height: 2.5em;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SectionDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
  font-family: ${FONTS.SECONDARY};
  line-height: 1.4;
`;

// Add preview button styles from OfertaStep2
const ButtonArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 0;
  width: 70%;
  margin-left: auto;
`;

const PreviewLinkContainer = styled.div`
  margin-bottom: 1rem;
`;

const PreviewButton = styled.button`
  background-color: transparent;
  color: #ff9980;
  border: none;
  border-radius: 0;
  text-decoration: underline;
  text-decoration-color: #ff9980;
  text-decoration-thickness: 2px;
  padding: 0.5rem 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0;
  cursor: pointer;
  font-family: ${FONTS.SECONDARY};
  
  &:hover {
    background-color: transparent;
    color: #ff7f66;
    text-decoration-color: #ff7f66;
  }
  
  &:focus {
    outline: none;
  }
`;

const CounterOfferPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [error, setError] = useState(null);
  const [counterOfferText, setCounterOfferText] = useState("");
  const [confidentiality, setConfidentiality] = useState("no");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const fetchOfferData = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        
        if (!token) {
          setError({
            title: "Token no encontrado",
            message: "No se ha proporcionado un token válido para acceder a la oferta."
          });
          return;
        }
        
        const response = await validateOfferToken(token);
        
        if (response && response.valid) {
          const offerData = {
            ...response.offer,
            token
          };
          
          setOffer(offerData);
          
          // Establecer la confidencialidad inicial según la oferta original
          // Si la oferta original es confidencial, Silvana NO puede cambiarla
          // Si la oferta original NO es confidencial, Silvana puede cambiarla a "Sí"
          const originalConfidentiality = offerData.confidential || offerData.offer_confidentiality;
          setConfidentiality(originalConfidentiality ? "si" : "no");
          
          // Check authentication
          if (!isAuthenticated) {
            sessionStorage.setItem("pendingOfferToken", token);
            sessionStorage.setItem("pendingOfferAction", "counteroffer");
            sessionStorage.setItem("pendingRedirectUrl", `/offers/counteroffer?token=${token}`);
            navigate("/login", { state: { from: location } });
            return;
          }
        } else {
          setError({
            title: "Oferta no disponible",
            message: response?.message || "No se puede acceder a esta oferta."
          });
        }
      } catch (err) {
        console.error("Error fetching offer:", err);
        setError({
          title: "Error al cargar la oferta",
          message: "Ha ocurrido un error al intentar cargar los detalles de la oferta."
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchOfferData();
  }, [location.search, isAuthenticated, navigate]);

  const handleSubmit = () => {
    if (!counterOfferText.trim()) {
      return;
    }
    if (!acceptTerms) {
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setIsProcessing(true);
    setShowConfirmation(false);
    
    try {
      // Incluir la confidencialidad en la llamada API
      const confidentialityValue = confidentiality === "si";
      await counterOffer(offer.id, counterOfferText.trim(), confidentialityValue);
      
      setSuccessMessage({
        title: "Su contraoferta se envió",
        message: "Su contraoferta ha sido registrada correctamente."
      });
      
      // Redirigir a "Mis Expedientes" en lugar de home
      setTimeout(() => {
        navigate("/offers");
      }, 3000);
    } catch (error) {
      setError({
        title: "Error al enviar contraoferta",
        message: error.message || "Ha ocurrido un error al intentar enviar la contraoferta."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const getPreviewContent = () => {
    return counterOfferText || "";
  };

  // Función para determinar si la confidencialidad puede ser modificada
  const canChangeConfidentiality = () => {
    // Si la oferta original es confidencial, Silvana NO puede cambiarla
    // Esta regla se aplica independientemente de si la oferta tiene archivos adjuntos o no
    const originalConfidentiality = offer?.confidential || offer?.offer_confidentiality;
    
    // Si la oferta original es confidencial, NO se puede cambiar
    if (originalConfidentiality) {
      return false;
    }
    
    // Si la oferta original NO es confidencial, se puede cambiar a "Sí"
    return true;
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <LoadingIndicator message="Cargando detalles de la oferta..." />
        </ContentArea>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <NotificationMessage 
            title={error.title} 
            message={error.message} 
            isError={true} 
          />
        </ContentArea>
      </PageContainer>
    );
  }

  if (successMessage) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <NotificationMessage 
            title={successMessage.title} 
            message={successMessage.message} 
            isError={false} 
          />
        </ContentArea>
      </PageContainer>
    );
  }

  if (!offer) {
    return (
      <PageContainer>
        <Sidebar>
          <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
        </Sidebar>
        <ContentArea>
          <NotificationMessage 
            title="Oferta no disponible" 
            message="No se ha podido cargar la información de la oferta." 
            isError={true} 
          />
        </ContentArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Sidebar>
        <SidebarLogoStyled src={SidebarLogo} alt="Logo" />
      </Sidebar>
      
      <ContentArea>
        <ContentContainer>
          <HeaderContainer>
            <Title>Contraoferta</Title>
            <Description>
              Responde a la Oferta Vinculante recibida con la contraoferta que consideres. Nos 
              encargaremos de enviarla de forma segura y confidencial. Antes de enviarla podrás 
              visualizar una vista preliminar del documento.
            </Description>
          </HeaderContainer>

          <TwoColumnLayout>
            <LeftColumn>
              <OfferDisplay offer={offer} />
            </LeftColumn>
            
            <RightColumn>
              <FormSection>
                <FormGroup>
                  <SectionTitle>Redactar contraoferta</SectionTitle>
                  <SectionDescription>
                    Puedes redactar tu contraoferta en este espacio. Recuerda que el documento digital 
                    en formato word, pdf o imagen que adjuntes prevalecerá sobre este texto.
                  </SectionDescription>
                  
                  <ToggleContainer>
                    <FormLabel>
                      Confidencialidad
                      <HelpButtonContainer>
                        <HelpButton type="button">i</HelpButton>
                        <TooltipText>
                          El artículo 9.2 a) de la L.O. 1/2025 establece que sólo puede dispensarse la confidencialidad cuando todas las partes (o sus abogados/as) de manera expresa y por escrito se hayan dispensado recíprocamente del deber de confidencialidad, por lo que si la otra parte considera que su respuesta debe ser confidencial, no podrás aportar al proceso la parte confidencial de esta oferta vinculante ni su respuesta
                        </TooltipText>
                      </HelpButtonContainer>
                    </FormLabel>
                    <SelectContainer>
                      <Select 
                        value={confidentiality} 
                        onChange={(e) => setConfidentiality(e.target.value)}
                        disabled={isProcessing || !canChangeConfidentiality()}
                        style={{ 
                          opacity: !canChangeConfidentiality() ? 0.6 : 1,
                          cursor: !canChangeConfidentiality() ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="no">No</option>
                        <option value="si">Sí</option>
                      </Select>
                      <SelectArrow>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <polygon points="12 17 4 7 20 7" />
                        </svg>
                      </SelectArrow>
                    </SelectContainer>
                  </ToggleContainer>
                  
                  {/* Mostrar mensaje explicativo si no se puede cambiar la confidencialidad */}
                  {!canChangeConfidentiality() && (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: '#666', 
                      marginTop: '0.5rem',
                      marginBottom: '1rem',
                      fontStyle: 'italic'
                    }}>
                      La confidencialidad no puede modificarse porque la oferta original es confidencial.
                    </div>
                  )}
                  
                  <TextAreaContainer>
                    <RichTextEditor
                      placeholder="Redacta aquí tu contraoferta"
                      value={counterOfferText}
                      onChange={(content) => setCounterOfferText(content)}
                      disabled={isProcessing}
                      minHeight="200px"
                    />
                  </TextAreaContainer>
                </FormGroup>
              </FormSection>

              <ButtonArea>
                {/* Checkbox for terms - aligned with buttons */}
                <ConfirmationCheckbox>
                  <CheckboxInput
                    type="checkbox" 
                    checked={acceptTerms} 
                    onChange={(e) => setAcceptTerms(e.target.checked)} 
                    id="terms-checkbox"
                    disabled={isProcessing}
                  />
                  <label htmlFor="terms-checkbox">
                    Recuerda que el artículo 17.1 de la L.O. 1/2025 establece que el envío de una oferta vinculante confidencial a la otra parte, queda obligada a cumplir la obligación que asume, una vez que la parte a la que va dirigida la acepta expresamente, y, en su caso, dicha aceptación expresa, tendrá carácter irrevocable.
                  </label>
                </ConfirmationCheckbox>
                
                <PreviewLinkContainer>
                  <PreviewButton onClick={handlePreview}>
                    Vista previa
                  </PreviewButton>
                </PreviewLinkContainer>
                
                <ButtonContainer>
                  <OutlineButton onClick={handleBack} disabled={isProcessing}>
                    Atrás
                  </OutlineButton>
                  <PrimaryButton onClick={handleSubmit} disabled={isProcessing}>
                    {isProcessing ? "Procesando..." : "Enviar"}
                  </PrimaryButton>
                </ButtonContainer>
              </ButtonArea>
            </RightColumn>
          </TwoColumnLayout>
        </ContentContainer>
        
        <Footer />
      </ContentArea>

      {showConfirmation && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ConfirmationText>
              Envía una contraoferta que podrá ser aceptada o rechazada por la otra parte. ¿Está seguro?
            </ConfirmationText>
            <ConfirmationButtons>
              <OutlineButton onClick={() => setShowConfirmation(false)}>
                No
              </OutlineButton>
              <PrimaryButton onClick={handleConfirmSubmit}>
                Sí
              </PrimaryButton>
            </ConfirmationButtons>
          </ConfirmationContent>
        </ConfirmationModal>
      )}

      {/* Preview Modal */}
      <PreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        formData={{
          ofertante: {
            nombre: offer?.offeror?.firstName || offer?.representedparty_name || '',
            apellidos: offer?.offeror?.lastNames || offer?.representedparty_last_name || '',
            identificacion: offer?.offeror?.idValue || offer?.representedparty_id || '',
            email: offer?.offeror?.email || offer?.representedparty_email || '',
            movil: offer?.offeror?.mobile || offer?.representedparty_phone || ''
          },
          contraparte: {
            nombre: offer?.counterparty?.firstName || offer?.counterparty_name || '',
            apellidos: offer?.counterparty?.lastNames || offer?.counterparty_last_name || '',
            identificacion: offer?.counterparty?.idValue || offer?.counterparty_id || '',
            email: offer?.counterparty?.email || offer?.counterparty_email || '',
            movil: offer?.counterparty?.mobile || offer?.counterparty_phone || ''
          },
          representacionContraparte: {
            activa: !!(offer?.lawyer?.firstName || offer?.lawyer_name || offer?.lawyer?.lastNames || offer?.lawyer_last_name),
            nombre: offer?.lawyer?.firstName || offer?.lawyer_name || '',
            apellidos: offer?.lawyer?.lastNames || offer?.lawyer_last_name || '',
            identificacion: offer?.lawyer?.idValue || offer?.lawyer_id || '',
            email: offer?.lawyer?.email || offer?.lawyer_email || '',
            movil: offer?.lawyer?.mobile || offer?.lawyer_phone || ''
          },
          referencia: offer?.reference || offer?.offer_reference || "",
          objeto: offer?.subject || offer?.offer_subject || "",
          cuantia: offer?.amount || offer?.offer_total_amount || "",
          confidencialidad: confidentiality === "si",
          plazoRespuesta: offer?.deadlineToRespond || offer?.offer_response_deadline || "",
          contenido: getPreviewContent()
        }}
        offerData={offer}
        user={user}
        isCounterOffer={true}
      />
    </PageContainer>
  );
};

export default CounterOfferPage; 